import { useEffect } from "react";

import { RouterProvider } from "react-router-dom";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ThemeProvider, I18nProvider } from "@rc-acs/shared/ui";

import { createAppRouter } from "@/router";

import { useAuthStore } from "@/hooks/useAuth";
import { CoreTimezoneProvider } from "@/hooks/useCoreTimezone";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const router = createAppRouter(queryClient, [
  window.location.pathname + window.location.search + window.location.hash,
]);

export function App() {
  const checkAuth = useAuthStore((s) => s.checkAuth);

  useEffect(() => {
    // Skip auth check on public routes (login page) — no session to validate,
    // and calling /api/sso/refresh here causes a redirect loop when the 401
    // interceptor in api.ts hard-navigates back to /login.
    if (window.location.pathname === "/login") {
      useAuthStore.setState({ isLoading: false });
      return;
    }
    checkAuth();
  }, [checkAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <CoreTimezoneProvider>
          <ThemeProvider defaultTheme="light" storageKey="rc-acs-theme" useSystemPreference>
            <RouterProvider router={router} />
          </ThemeProvider>
        </CoreTimezoneProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
